/**
 * Client SSE cho AI Agent Hub. Dùng fetch (EventSource không gửi được header Authorization).
 * Tự nối lại 1 lần bằng `Last-Event-ID` qua `/runs/{id}/events` nếu kết nối rơi giữa chừng.
 */
import { getAuthorizedHeaders, resolveApiUrl } from '@/services/base-service';
import { aiHubGatewayHeaders } from '@/configs/aiHubApiOrigin';
import { tokenService } from '@/services/token/token-service';
import type { AiHubEvent } from '@/types/ai-agent';

export interface StreamOptions {
  signal?: AbortSignal;
  onEvent: (event: AiHubEvent) => void;
}

export class AiHubStreamError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
  }
}

/** Tách khung SSE (`id:`/`event:`/`data:` + dòng trống). Export để test. */
export const parseSseChunk = (
  buffer: string,
): { events: AiHubEvent[]; rest: string } => {
  const events: AiHubEvent[] = [];
  const frames = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = frames.pop() ?? '';
  for (const frame of frames) {
    let id = 0;
    let type = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const idx = line.indexOf(':');
      const field = idx === -1 ? line : line.slice(0, idx);
      const value = idx === -1 ? '' : line.slice(idx + 1).replace(/^ /, '');
      if (field === 'id') id = Number(value) || 0;
      else if (field === 'event') type = value;
      else if (field === 'data') dataLines.push(value);
    }
    if (!dataLines.length) continue;
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      data = { raw: dataLines.join('\n') };
    }
    events.push({ id, type, data });
  }
  return { events, rest };
};

export const readStream = async (response: Response, onEvent: (event: AiHubEvent) => void): Promise<void> => {
  const reader = response.body?.getReader();
  if (!reader) throw new AiHubStreamError('Trình duyệt không hỗ trợ stream');
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseSseChunk(buffer);
    buffer = parsed.rest;
    parsed.events.forEach(onEvent);
  }
};

export const open = async (
  method: 'GET' | 'POST',
  path: string,
  body: unknown,
  signal: AbortSignal | undefined,
  lastEventId?: number,
): Promise<Response> => {
  const headers = await getAuthorizedHeaders();
  if (!headers) {
    await tokenService.handleTokenExpired();
    throw new AiHubStreamError('Phiên đăng nhập đã hết hạn.', 401);
  }
  const response = await fetch(resolveApiUrl(path), {
    method,
    headers: {
      ...headers,
      ...aiHubGatewayHeaders(),
      Accept: 'text/event-stream',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(lastEventId ? { 'Last-Event-ID': String(lastEventId) } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!response.ok) {
    let message = `Lỗi ${response.status}`;
    try {
      const json = await response.json();
      message = json?.message || json?.detail || message;
    } catch {
      /* body không phải JSON */
    }
    if (response.status === 401) await tokenService.handleTokenExpired();
    throw new AiHubStreamError(message, response.status);
  }
  return response;
};

/** POST một endpoint SSE của hub và phát từng sự kiện; resolve khi run kết thúc. */
export const streamSse = async (path: string, body: unknown, options: StreamOptions): Promise<void> => {
  const tracker = { lastId: 0, runId: '', finished: false };
  const onEvent = (event: AiHubEvent) => {
    tracker.lastId = event.id || tracker.lastId;
    if (event.type === 'run.started') tracker.runId = String(event.data.run_id ?? '');
    if (event.type === 'run.finished') tracker.finished = true;
    options.onEvent(event);
  };
  try {
    await readStream(await open('POST', path, body, options.signal), onEvent);
  } catch (error) {
    // Lỗi HTTP / hủy chủ động: trả về cho caller. Rớt mạng giữa chừng: thử nối lại bên dưới.
    if (options.signal?.aborted || error instanceof AiHubStreamError || !tracker.runId) throw error;
  }
  if (tracker.finished || !tracker.runId || options.signal?.aborted) return;
  const resumed = await open('GET', `/v1/aihub/runs/${tracker.runId}/events`, undefined, options.signal, tracker.lastId);
  await readStream(resumed, onEvent);
};
